/**
 * Shared constants for the Akem platform
 */
import { SubscriptionTier } from "../types/subscription.js";
/**
 * API rate limits and features by subscription tier
 */
export const TIER_FEATURES = {
    [SubscriptionTier.BASIC_CONNECT]: {
        polygon: {
            realtime: false,
            delay: 15, // 15 minutes
            history: 30, // 30 days
            callsPerMinute: 5,
            websocketChannels: 1,
        },
        perplexity: {
            searchesPerDay: 10,
            monitoring: false,
            realTimeAlerts: false,
        },
        gemini: {
            model: "gemini-pro",
            orderDepth: 1,
            requestsPerDay: 50,
            batchProcessing: false,
        },
        ui: {
            advancedCharts: false,
            customDashboards: false,
            exportData: false,
            apiAccess: false,
        },
    },
    [SubscriptionTier.INVESTOR_PRO]: {
        polygon: {
            realtime: false,
            delay: 1, // 1 minute
            history: 365, // 1 year
            callsPerMinute: 30,
            websocketChannels: 10,
        },
        perplexity: {
            searchesPerDay: 100,
            monitoring: true,
            realTimeAlerts: false,
        },
        gemini: {
            model: "gemini-pro",
            orderDepth: 2,
            requestsPerDay: 500,
            batchProcessing: false,
        },
        ui: {
            advancedCharts: true,
            customDashboards: true,
            exportData: true,
            apiAccess: false,
        },
    },
    [SubscriptionTier.TRADER_ELITE]: {
        polygon: {
            realtime: true,
            delay: 0,
            history: 1095, // 3 years
            callsPerMinute: 150,
            websocketChannels: 50,
        },
        perplexity: {
            searchesPerDay: 1000,
            monitoring: true,
            realTimeAlerts: true,
        },
        gemini: {
            model: "gemini-pro",
            orderDepth: 3,
            requestsPerDay: 5000,
            batchProcessing: true,
        },
        ui: {
            advancedCharts: true,
            customDashboards: true,
            exportData: true,
            apiAccess: true,
        },
    },
    [SubscriptionTier.ALPHA_SUITE]: {
        polygon: {
            realtime: true,
            delay: 0,
            history: -1, // unlimited
            callsPerMinute: -1, // unlimited
            websocketChannels: 100,
        },
        perplexity: {
            searchesPerDay: -1, // unlimited
            monitoring: true,
            realTimeAlerts: true,
        },
        gemini: {
            model: "gemini-ultra",
            orderDepth: 4,
            requestsPerDay: -1, // unlimited
            batchProcessing: true,
        },
        ui: {
            advancedCharts: true,
            customDashboards: true,
            exportData: true,
            apiAccess: true,
        },
    },
};
/**
 * API endpoints
 */
export const API_ENDPOINTS = {
    MARKET: {
        QUOTE: "/api/market/quote",
        HISTORY: "/api/market/history",
        STREAM: "/api/market/stream",
        COMPANY: "/api/market/company",
        STATUS: "/api/market/status",
    },
    NEWS: {
        SEARCH: "/api/news/search",
        COMPANY: "/api/news/company",
        TRENDING: "/api/news/trending",
        MONITOR: "/api/news/monitor",
    },
    ANALYSIS: {
        IMPACT: "/api/analysis/impact",
        STORY: "/api/analysis/story",
        ALERTS: "/api/analysis/alerts",
        SENTIMENT: "/api/analysis/sentiment",
    },
    USER: {
        PROFILE: "/api/user/profile",
        PREFERENCES: "/api/user/preferences",
        SUBSCRIPTION: "/api/user/subscription",
        WATCHLISTS: "/api/user/watchlists",
        ALERTS: "/api/user/alerts",
    },
    AUTH: {
        LOGIN: "/api/auth/login",
        REGISTER: "/api/auth/register",
        REFRESH: "/api/auth/refresh",
        LOGOUT: "/api/auth/logout",
        VERIFY: "/api/auth/verify",
    },
};
/**
 * WebSocket event types
 */
export const WS_EVENTS = {
    // Market data
    QUOTE_UPDATE: "quote_update",
    TRADE_UPDATE: "trade_update",
    BAR_UPDATE: "bar_update",
    // Analysis
    IMPACT_ANALYSIS: "impact_analysis",
    SENTIMENT_UPDATE: "sentiment_update",
    STORY_UPDATE: "story_update",
    // Alerts
    PRICE_ALERT: "price_alert",
    NEWS_ALERT: "news_alert",
    IMPACT_ALERT: "impact_alert",
    // System
    CONNECTION_STATUS: "connection_status",
    ERROR: "error",
};
/**
 * Time periods for various operations
 */
export const TIME_PERIODS = {
    CACHE_TTL: {
        MARKET_DATA: 5, // seconds
        COMPANY_INFO: 86400, // 24 hours
        NEWS_SEARCH: 900, // 15 minutes
        AI_ANALYSIS: 3600, // 1 hour
    },
    RATE_LIMIT_WINDOW: 60, // seconds
    SESSION_TIMEOUT: 3600, // 1 hour
    API_TIMEOUT: 30, // seconds
};
/**
 * Supported asset classes
 */
export const ASSET_CLASSES = [
    "stocks",
    "options",
    "crypto",
    "forex",
    "commodities",
    "bonds",
    "etfs",
    "indices",
];
/**
 * Supported markets
 */
export const MARKETS = ["US", "EU", "ASIA", "CRYPTO"];
/**
 * Technical indicator types
 */
export const INDICATORS = [
    "SMA",
    "EMA",
    "RSI",
    "MACD",
    "BB", // Bollinger Bands
    "STOCH",
    "ADX",
    "CCI",
    "WILLIAMS_R",
    "ROC",
];
/**
 * Chart timeframes
 */
export const TIMEFRAMES = [
    "1m",
    "5m",
    "15m",
    "30m",
    "1h",
    "4h",
    "1d",
    "1w",
    "1M",
];
/**
 * Profile constants
 */
export const GLOBAL_NEWS_PROFILE_ID = "__news:global__";
/**
 * Error codes
 */
export const ERROR_CODES = {
    // Authentication
    UNAUTHORIZED: "UNAUTHORIZED",
    INVALID_TOKEN: "INVALID_TOKEN",
    TOKEN_EXPIRED: "TOKEN_EXPIRED",
    // Rate limiting
    RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",
    QUOTA_EXCEEDED: "QUOTA_EXCEEDED",
    // Data
    INVALID_TICKER: "INVALID_TICKER",
    NO_DATA_AVAILABLE: "NO_DATA_AVAILABLE",
    DATA_TOO_OLD: "DATA_TOO_OLD",
    // Analysis
    ANALYSIS_FAILED: "ANALYSIS_FAILED",
    ANALYSIS_TIMEOUT: "ANALYSIS_TIMEOUT",
    INSUFFICIENT_TIER: "INSUFFICIENT_TIER",
    // General
    INTERNAL_ERROR: "INTERNAL_ERROR",
    INVALID_REQUEST: "INVALID_REQUEST",
    SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
};
